import * as React from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { Heading, Text, ThemeCss } from "gatsby-interface"
import { Buttons, Cta } from "./Ctas"
import {
  sectionCss,
  heroTitleCss,
  eyebrowHeadingCss,
  heroLedeCss,
  iconEyebrowHeadingCss,
  maxSectionWidth,
} from "../style-utils"
import { AnchorSection } from "./AnchorSection"
import { ColorSchemeCss } from "../color-schemes"
import { getSSLPSanitizedHtml } from "../utils"
import { ContentfulSSLPImage } from "../types"

export interface HeroProps {
  title: string
  eyebrow?: string
  lede?: string
  iconSrc?: string
  leftBackgroundSrc?: string
  rightBackgroundSrc?: string
  primaryCta?: Cta
  secondaryCta?: Cta
  images?: ContentfulSSLPImage[]
}

const containerCss: ColorSchemeCss = theme => [
  sectionCss(theme),
  {
    position: "relative",
    [theme.mediaQueries.phablet]: {
      paddingBottom: theme.space[10],
      paddingTop: theme.space[10],
    },
    [theme.mediaQueries.desktop]: {
      paddingBottom: theme.space[15],
      paddingTop: theme.space[15],
    },
  },
]

const wrapperCss: ThemeCss = theme => ({
  // background: theme.colors.blackFade[5],
  display: `grid`,
  gap: theme.space[7],
  position: "relative",
  zIndex: 2,
  textAlign: "center",

  [theme.mediaQueries.desktop]: {
    width: "52rem",
    margin: `0 auto`,
  },
})

const titleCss: ColorSchemeCss = theme => [
  heroTitleCss(theme),
  {
    maxWidth: "24ch",
    marginLeft: "auto",
    marginRight: "auto",
  },
]

const contentCss: ColorSchemeCss = theme => [
  heroLedeCss(theme),
  {
    color: theme.colorScheme.text,
    margin: `0 auto`,
    maxWidth: maxSectionWidth.default,
    whiteSpace: `pre-wrap`,
  },
]

const iconCss: ColorSchemeCss = theme => ({
  maxHeight: theme.space[13], // 72px
  maxWidth: theme.space[13], // 72px
  margin: `auto`,
  marginBottom: theme.space[7],
  textAlign: "center",
  color: theme.colorScheme.accent,
})

const leftImageCss: ThemeCss = _ => ({
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  margin: 0,
  maxHeight: "100%",
})

const rightImageCss: ThemeCss = _ => ({
  position: "absolute",
  right: 0,
  top: 0,
  bottom: 0,
  margin: 0,
  maxHeight: "100%",
})

function Hero({
  title,
  eyebrow,
  iconSrc,
  rightBackgroundSrc,
  leftBackgroundSrc,
  lede,
  primaryCta,
  secondaryCta,
  images = [],
}: HeroProps) {
  const [image] = images || []

  return (
    <AnchorSection id={title} css={containerCss}>
      {leftBackgroundSrc ? (
        <img
          src={leftBackgroundSrc}
          aria-hidden={true}
          alt=""
          css={leftImageCss}
        />
      ) : null}
      {rightBackgroundSrc ? (
        <img
          src={rightBackgroundSrc}
          aria-hidden={true}
          alt=""
          css={rightImageCss}
        />
      ) : null}

      <div css={wrapperCss}>
        <div css={iconEyebrowHeadingCss}>
          {iconSrc ? (
            <div css={iconCss}>
              <img src={iconSrc} aria-hidden={true} alt="" />
            </div>
          ) : null}
          {eyebrow ? <Text css={eyebrowHeadingCss}>{eyebrow}</Text> : null}
          {title ? (
            <Heading
              as="h1"
              css={titleCss}
              dangerouslySetInnerHTML={{
                __html: getSSLPSanitizedHtml(title),
              }}
            />
          ) : null}
        </div>
        {lede ? (
          <Text
            as="div"
            css={contentCss}
            dangerouslySetInnerHTML={{
              __html: getSSLPSanitizedHtml(lede),
            }}
          />
        ) : null}
        <Buttons primaryCta={primaryCta} secondaryCta={secondaryCta} />
        {image && (
          <div
            css={theme => ({
              marginTop: theme.space[7],
            })}
          >
            <GatsbyImage image={image.constrained} alt="" />
          </div>
        )}
      </div>
    </AnchorSection>
  )
}

export default Hero
